<script setup lang="ts">
import type { Component } from "vue";
import { computed, onUnmounted } from "vue";

import AgentCategoryChip from "~/components/agent/AgentCategoryChip.vue";
import Avatar from "~/components/dumb/Avatar.vue";
import StatusPill from "~/components/dumb/StatusPill.vue";
import { getConfiguredLocalAgent, getLocalAgentDisplay, getLocalAgentModelSettings } from "~/constants/localAgent";
import { getModelChipLabel, MODEL_TO_ICON } from "~/constants/model";
import { DartAiIcon, LinkIcon, RecentIcon } from "~/icons";
import { ExecutionMode, UserStatus } from "~/shared/enums";
import type { User } from "~/shared/types";
import { makeLocalTimeForTimezoneRef } from "~/utils/time";
import { getUserDescriptionText, getUserDisplayName, isAgentUser, isNativeAgent } from "~/utils/user";

const props = withDefaults(
  defineProps<{
    user: User;
    compact?: boolean;
  }>(),
  { compact: false }
);

const isAgent = computed(() => isAgentUser(props.user));
const displayName = computed(() => getUserDisplayName(props.user) || (isAgent.value ? "Agent" : props.user.email));

const subtitle = computed(() => (isAgent.value ? null : props.user.email));
const descriptionText = computed(() => getUserDescriptionText(props.user));

const nativeAgentDetail = computed<{ label: string; icon: Component } | null>(() => {
  const { agent } = props.user;
  if (!isNativeAgent(props.user) || !agent || agent.executionMode === ExecutionMode.NONE) {
    return null;
  }
  if (agent.executionMode === ExecutionMode.API) {
    const url = agent.config.apiWebhook?.url.trim();
    return url ? { label: url.replace(/^https?:\/\//, ""), icon: LinkIcon } : null;
  }
  const localAgent = getConfiguredLocalAgent(agent);
  const modelSettings = localAgent
    ? getLocalAgentModelSettings(localAgent, agent.model, agent.thinkingLevel, agent.localState)
    : null;
  const model = modelSettings?.model ?? agent.model;
  const localAgentDisplay = modelSettings?.modelOptions.length === 0 ? getLocalAgentDisplay(localAgent) : null;
  return {
    label: getModelChipLabel(
      model,
      modelSettings?.thinkingLevelOptions.length === 0
        ? undefined
        : (modelSettings?.thinkingLevel ?? agent.thinkingLevel)
    ),
    icon: localAgentDisplay?.icon ?? MODEL_TO_ICON.get(model) ?? DartAiIcon,
  };
});

const role = computed(() => (isAgent.value ? null : props.user.role));

const isDisabledAgent = computed(() => isAgent.value && props.user.status === UserStatus.AGENT_DISABLED);

const timezoneRef = computed(() => (isAgent.value ? null : props.user.timezone));
const localTime = makeLocalTimeForTimezoneRef(timezoneRef);
onUnmounted(() => localTime.value.destroy());

const showLocalTime = computed(() => !!timezoneRef.value && !!localTime.value.short);
const localTimeTooltip = computed(() => (timezoneRef.value ? localTime.value.full : ""));
</script>

<template>
  <div class="flex flex-col gap-2.5" :class="compact && 'overflow-hidden'">
    <div class="flex min-w-0 items-start gap-3">
      <Avatar :user="user" class="size-11 shrink-0" />
      <div class="flex min-w-0 flex-1 flex-col gap-0.5">
        <div class="flex min-w-0 items-center gap-1.5">
          <span :title="displayName" class="truncate text-sm font-semibold text-md">{{ displayName }}</span>
          <StatusPill v-if="isDisabledAgent" text="Disabled" :is-pulsing="false" class="shrink-0" />
        </div>
        <AgentCategoryChip v-if="isAgent" :agent="user" />
        <div v-else-if="!compact && (subtitle || role)" class="flex min-w-0 flex-col gap-0.5 text-xs text-lt">
          <span v-if="subtitle" :title="subtitle" class="truncate">{{ subtitle }}</span>
          <span v-if="role" class="truncate">{{ role }}</span>
        </div>
      </div>
    </div>

    <div v-if="!compact && showLocalTime" class="flex items-center gap-1.5 text-xs text-lt" :title="localTimeTooltip">
      <RecentIcon class="icon-xs shrink-0 text-vlt" />
      <span class="truncate">{{ `${localTime.short} local time` }}</span>
    </div>

    <div v-if="nativeAgentDetail" class="flex items-center gap-1.5 text-xs text-lt" :title="nativeAgentDetail.label">
      <component :is="nativeAgentDetail.icon" class="icon-xs shrink-0 rounded-sm text-vlt" />
      <span class="truncate">{{ nativeAgentDetail.label }}</span>
    </div>

    <div
      v-if="descriptionText"
      class="break-words text-xs hyphens-auto text-lt"
      :class="compact ? (nativeAgentDetail ? 'line-clamp-2' : 'line-clamp-3') : 'line-clamp-6'"
      :title="descriptionText">
      {{ descriptionText }}
    </div>
  </div>
</template>
